import AccountDropdown from '@hydra/common/app/TopNavigation/AccountDropdown';
import { useFeatureFlags } from '@hydra/common/components/FeatureFlags/featureFlagContext';
import Translate, { useI18n } from '@hydra/common/components/i18n';
import Icon from '@hydra/common/components/Icon';
import MagicBar from '@hydra/common/components/MagicBar';
import { useUserContext } from '@hydra/common/context/user';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import HelpDropdown from '../TopNavigation/HelpDropdown';

const useStyles = makeStyles(theme => ({
  userTools: {
    display: 'flex',
    alignItems: 'center',
    flex: '1 0 0'
  },
  userToolsNavigationExpandedMagicBar: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex'
    }
  },
  branding: {
    flex: '1 0 0',
    textAlign: 'center',
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'flex'
    }
  },
  userActions: {
    display: 'flex',
    marginLeft: theme.spacing(1),
    justifyContent: 'flex-end',
    flex: '0 0 0'
  },
  brandingExpandedMagicbar: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  flexBox: {
    display: 'flex'
  },
  accountDropdownButtonExpandedMagicbar: {
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  closeMagicbarButton: {
    display: 'none'
  },
  closeMagicbarButtonExpandedMagicbar: {
    [theme.breakpoints.down('xs')]: {
      display: 'flex'
    }
  },
  brandingIcon: {
    height: 20,
    width: 110,
    marginTop: -6,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    color: theme.palette.common.white,
    opacity: 0.5,
    transition: theme.transitions.create(
      ['opacity'],
      { duration: theme.transitions.duration.complex }
    ),
    '&:hover, &:active, &:focus': {
      opacity: 0.8
    }
  },
  xsDownHidden: {
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  smUpHidden: {
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  mdUpHidden: {
    [theme.breakpoints.up('md')]: {
      display: 'none'
    }
  },
  iconButton: {
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1)
    }
  },
  homeButton: {
    marginRight: theme.spacing(1)
  },
  header: {
    pointerEvents: 'none',
    opacity: '1 !important', // Avoid opacity when element is disabled
    '& > span': {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.fontSizeLarge
    },
    '&:focus, &:hover, &:active': {
      textDecoration: 'none'
    }
  },
  helpDropdownButton: {
    color: theme.palette.common.white
  },
  helpMenuItem: {
    minWidth: 255,
    maxWidth: 255,
    width: 255,
    '&:hover': {
      '& $helpListItemIcon': {
        '& > svg': {
          fill: theme.palette.primary.main
        }
      },
      '& $externalListIconItem': {
        '& > svg': {
          display: 'inline-block',
          fill: theme.palette.primary.main
        }
      }
    }
  },
  helpListItemIcon: {},
  externalListIconItem: {
    marginLeft: 'auto',
    minWidth: theme.spacing(3),
    '& > svg': {
      display: 'none',
      width: theme.spacing(2),
      height: theme.spacing(2)
    }
  }
}));

function ContactHelp ({ classes }) {
  return (
    <HelpDropdown
      contentColor='white'
      renderTrigger={({ ...props }) => (
        <Button
          {...props}
        >
          <Typography noWrap className={classes.helpDropdownButton}>
            <Translate content='help_dropdown_item_help' />
          </Typography>
        </Button>
      )}
    />
  );
}

export function FluidNavigationBar ({ sideNavToggle }) {
  const { user } = useUserContext();
  const [expandedMagicbar, setExpandedMagicbar] = useState(false);
  const classes = useStyles();
  const { locale, translate } = useI18n();
  const featureFlags = useFeatureFlags();

  function onMagicbarFocus () {
    if (!expandedMagicbar) {
      handleMagicbar();
    }
  }

  function onMagicbarBlur () {
    handleMagicbar();
  }

  function handleMagicbar () {
    setExpandedMagicbar(!expandedMagicbar);
  }

  const accountDropdownClassNames = classnames(
    classes.flexBox,
    { [classes.accountDropdownButtonExpandedMagicbar]: expandedMagicbar },
    'nr-nav-user'
  );

  const overviewLink = `/${locale}/overview`;

  const userTools = (
    <div className={classes.userTools}>
      <div className={classnames(
        classes.flexBox,
        { [classes.userToolsNavigationExpandedMagicBar]: expandedMagicbar }
      )}
      >
        {sideNavToggle
          ? (
            <IconButton
              className={classnames(
                classes.iconButton,
                classes.mdUpHidden
              )}
              onClick={sideNavToggle}
            >
              <Icon type='menu' color='white' />
            </IconButton>
            )
          : null}
        <IconButton
          href={overviewLink}
          title={`Woorank | ${translate('baseline')}`}
          className={classnames(
            classes.iconButton,
            classes.homeButton,
            'nr-nav-home'
          )}
        >
          <Icon type='home' color='white' />
        </IconButton>
      </div>
      <MagicBar
        language={locale}
        focused={expandedMagicbar}
        onFocus={onMagicbarFocus}
        onBlur={onMagicbarBlur}
      />
    </div>
  );

  const branding = (
    <div className={classnames(
      classes.branding,
      { [classes.brandingExpandedMagicbar]: expandedMagicbar }
    )}
    >
      <ButtonBase
        href={overviewLink}
        title={`Woorank | ${translate('baseline')}`}
      >
        <Typography variant='srOnly'>
          {`Woorank | ${translate('baseline')}`}
        </Typography>
        <Icon
          className={classnames(classes.brandingIcon)}
          type='logo-nocolor'
          viewBox='0 0 110 20'
        />
      </ButtonBase>
    </div>
  );

  const userActions = (
    <div className={classes.userActions}>
      <div className={classnames(classes.xsDownHidden, classes.flexBox, 'nr-nav-help')}>
        <ContactHelp
          locale={locale}
          featureFlags={featureFlags}
          classes={classes}
        />
      </div>
      {
        user && user.email && (
          <AccountDropdown
            renderTrigger={({ className, ...props }) => (
              <IconButton
                id='nav-user'
                className={classnames(
                  className,
                  accountDropdownClassNames
                )}
                {...props}
              >
                <Icon type='user-empty' color='white' />
              </IconButton>
            )}
          />
        )
      }
    </div>
  );

  const closeButton = (
    <IconButton
      onClick={handleMagicbar}
      className={classnames(
        classes.iconButton,
        classes.closeMagicbarButton,
        { [classes.closeMagicbarButtonExpandedMagicbar]: expandedMagicbar }
      )}
    >
      <Icon type='cross' size='md' color='white' />
    </IconButton>
  );

  return (
    <AppBar>
      <Toolbar>
        <Grid
          container
          direction='row'
          justify='space-between'
          alignItems='center'
          wrap='nowrap'
        >
          {userTools}
          {branding}
          {userActions}
          {closeButton}
        </Grid>
      </Toolbar>
    </AppBar>
  );
}

FluidNavigationBar.propTypes = {
  user: PropTypes.object,
  sideNavToggle: PropTypes.func
};

export default React.memo(FluidNavigationBar);
